import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Status badge config
const STATUS_BADGES = {
  emerging: { label: "Emerging", bg: "bg-emerald-500/20", text: "text-emerald-400" },
  peaking: { label: "Peaking", bg: "bg-blue-500/20", text: "text-blue-400" },
  fading: { label: "Fading", bg: "bg-amber-500/20", text: "text-amber-400" },
  anomaly: { label: "Anomaly", bg: "bg-red-500/20", text: "text-red-400" },
  disqualified: { label: "Disqualified", bg: "bg-red-500/20", text: "text-red-400" },
};

const PLATFORM_BADGES = {
  meta: { label: "Meta", bg: "bg-blue-600/20", text: "text-blue-400" },
  tiktok: { label: "TikTok", bg: "bg-pink-500/20", text: "text-pink-400" },
  youtube: { label: "YouTube", bg: "bg-red-500/20", text: "text-red-400" },
  google: { label: "Google", bg: "bg-yellow-500/20", text: "text-yellow-400" },
  native: { label: "Native", bg: "bg-purple-500/20", text: "text-purple-400" },
  amazon: { label: "Amazon", bg: "bg-amber-500/20", text: "text-amber-400" },
};

const GhostReconOfferDetail = () => {
  const { offerId } = useParams();
  const navigate = useNavigate();
  const [offer, setOffer] = useState(null);
  const [intel, setIntel] = useState(null);
  const [velocityHistory, setVelocityHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeIntelTab, setActiveIntelTab] = useState("funnel");
  const [actionLoading, setActionLoading] = useState(false);
  const [competitorAds, setCompetitorAds] = useState([]);
  const [competitorAdsLoading, setCompetitorAdsLoading] = useState(false);
  const [decomposingId, setDecomposingId] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const [offerRes, intelRes] = await Promise.all([
        axios.get(`${API}/ghost-recon/offers/${offerId}`),
        axios.get(`${API}/ghost-recon/offers/${offerId}/intelligence-brief`),
      ]);
      setOffer(offerRes.data);
      setIntel(intelRes.data);
      // The dedicated /velocity endpoint was removed from the backend. It was
      // still requested here inside the same Promise.all, so its 404 rejected
      // the whole batch and errored the page. The brief already carries the
      // same records from the same source (get_velocity_history).
      setVelocityHistory(intelRes.data?.velocity_history || []);
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to load offer");
    } finally {
      setLoading(false);
    }
  }, [offerId]);

  const fetchCompetitorAds = useCallback(async (offerData) => {
    if (!offerData) return;
    setCompetitorAdsLoading(true);
    try {
      const res = await axios.post(`${API}/swipekit/offer-search`, {
        offer_name: offerData.name || offerData.product_name || "",
        vertical: offerData.vertical || offerData.product_category || "",
        keywords: offerData.search_term || "",
      });
      setCompetitorAds(res.data.ads || []);
    } catch (e) {
      console.error("Failed to fetch competitor ads:", e);
    } finally {
      setCompetitorAdsLoading(false);
    }
  }, []);

  const handleDecompose = async (swipekitId) => {
    setDecomposingId(swipekitId);
    try {
      const res = await axios.post(`${API}/swipekit/decompose/${swipekitId}`);
      setCompetitorAds(prev => prev.map(ad =>
        ad.swipekit_id === swipekitId ? { ...ad, decomposition: res.data.decomposition } : ad
      ));
    } catch (e) {
      console.error("Decomposition failed:", e);
    } finally {
      setDecomposingId(null);
    }
  };

  useEffect(() => { fetchData(); }, [fetchData]);

  // Fetch competitor ads when offer loads
  useEffect(() => {
    if (offer) {
      fetchCompetitorAds(offer);
    }
  }, [offer, fetchCompetitorAds]);

  const handleAction = async (actionType) => {
    setActionLoading(true);
    try {
      await axios.post(`${API}/ghost-recon/offers/${offerId}/action`, {
        action_type: actionType,
      });
      fetchData();
    } catch (err) {
      console.error(`Failed to record action: ${actionType}`, err);
    } finally {
      setActionLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-emerald-500"></div>
      </div>
    );
  }

  if (error || !offer) {
    return (
      <div className="text-center py-12">
        <p className="text-red-400 mb-4">{error || "Offer not found"}</p>
        <button onClick={() => navigate("/ghost-recon")} className="text-emerald-400 hover:text-emerald-300">
          Back to Ghost Recon
        </button>
      </div>
    );
  }

  const scoring = offer.scoring || {};
  const totalScore = scoring.total || 0;
  const statusBadge = STATUS_BADGES[offer.status] || STATUS_BADGES.emerging;
  const platforms = offer.platforms || [];

  // Score color for GR. Composite max is 60 (ad_longevity 30 + competition 20 + angle 10).
  // WP4.5: these thresholds were 800/500/300 against a documented-but-never-real 1200
  // scale, so every offer fell through to the dimmest band — a perfect 60 looked like a 0.
  // Rebanded at the same proportions (66% / 42% / 25%) of the real maximum.
  const scoreColor = totalScore >= 40 ? "text-emerald-400" : totalScore >= 25 ? "text-blue-400" : totalScore >= 15 ? "text-amber-400" : "text-slate-400";

  // Velocity chart data (reverse to show oldest first)
  const chartData = [...velocityHistory].reverse().map((v) => ({
    date: new Date(v.created_at || v.scan_cycle).toLocaleDateString("en-US", { month: "short", day: "numeric" }),
    velocity: v.combined_velocity || 0,
    advertisers: v.advertiser_count || 0,
    adVolume: v.ad_volume || 0,
  }));

  return (
    <div className="space-y-6" data-testid="ghost-recon-offer-detail">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <button
            onClick={() => navigate("/ghost-recon")}
            className="text-slate-400 hover:text-white mb-2 flex items-center gap-1"
            data-testid="gr-back-button"
          >
            ← Ghost Recon
          </button>
          <h1 className="text-2xl font-bold text-white" data-testid="gr-offer-name">
            {offer.product_name}
          </h1>
          <div className="flex items-center gap-3 mt-2">
            <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${statusBadge.bg} ${statusBadge.text}`} data-testid="gr-status-badge">
              {statusBadge.label}
            </span>
            {offer.vertical && <span className="text-slate-500 text-sm">{offer.vertical}</span>}
            {offer.product_category && <span className="text-slate-500 text-sm">{offer.product_category}</span>}
            {offer.business_unit && <span className="text-slate-600 text-xs">{offer.business_unit}</span>}
          </div>
        </div>
        <div className="text-right">
          <div className={`text-4xl font-bold ${scoreColor}`} data-testid="gr-total-score">
            {totalScore}
          </div>
          <div className="text-sm text-slate-500">/ 1200 pts</div>
          <div className="flex items-center gap-2 mt-2">
            {["flagged", "watchlisted"].map((action) => (
              <button
                key={action}
                onClick={() => handleAction(action)}
                disabled={actionLoading}
                data-testid={`gr-action-${action}`}
                className="px-3 py-1.5 text-xs bg-slate-800 text-slate-300 hover:text-white rounded-lg hover:bg-slate-700 transition-colors disabled:opacity-50"
              >
                {action === "flagged" ? "Flag" : "Watchlist"}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Disqualification Banner */}
      {offer.disqualified && (
        <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4">
          <p className="text-red-400 font-medium">Disqualified</p>
          <p className="text-red-300 text-sm mt-1">{offer.disqualification_reason}</p>
        </div>
      )}

      {/* Key Metrics Row */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <MetricCard label="Retail Price" value={offer.retail_price ? `$${offer.retail_price.toFixed(0)}` : "—"} />
        <MetricCard label="Commission" value={offer.commission ? `$${offer.commission.toFixed(0)}` : "—"} sub={offer.commission_confirmed ? "Confirmed" : "Unconfirmed"} />
        <MetricCard label="Advertisers" value={offer.total_advertiser_count || 0} />
        <MetricCard label="Total Ads" value={offer.total_ad_count || 0} />
        <MetricCard label="Running Days" value={offer.ad_running_days || 0} />
      </div>

      {/* Platform Presence */}
      {platforms.length > 0 && (
        <div className="bg-slate-900/50 border border-slate-800 rounded-xl p-5" data-testid="platform-presence">
          <h3 className="text-base font-semibold text-white mb-3">Platform Presence</h3>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {platforms.map((p) => {
              const badge = PLATFORM_BADGES[p.platform] || { label: p.platform, bg: "bg-slate-500/20", text: "text-slate-400" };
              return (
                <div key={p.platform} className="bg-slate-800/50 rounded-lg p-3">
                  <div className="flex items-center gap-2 mb-2">
                    <span className={`px-2 py-0.5 rounded text-xs ${badge.bg} ${badge.text}`}>{badge.label}</span>
                  </div>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div>
                      <span className="text-slate-500 text-xs">Ads</span>
                      <p className="text-white font-medium">{p.ad_count}</p>
                    </div>
                    <div>
                      <span className="text-slate-500 text-xs">Advertisers</span>
                      <p className="text-white font-medium">{p.advertiser_count}</p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Scoring Breakdown + Velocity Chart */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* GR Scoring Breakdown */}
        <div className="bg-slate-900/50 border border-slate-800 rounded-xl p-5" data-testid="gr-scoring-breakdown">
          <h3 className="text-base font-semibold text-white mb-4">Scoring Breakdown</h3>
          <div className="space-y-3">
            <ScoreCriterion name="Commission/Margin" score={scoring.commission_margin || 0} max={400} note={scoring.commission_confirmed ? "Confirmed" : "Unconfirmed"} />
            <ScoreCriterion name="Competition Density" score={scoring.competition_density || 0} max={200} />
            <ScoreCriterion name="Angle Availability" score={scoring.angle_availability || 0} max={200} />
            <ScoreCriterion name="Ad Longevity" score={scoring.ad_longevity || 0} max={200} />
            <ScoreCriterion name="Velocity" score={scoring.velocity || 0} max={200} />
          </div>
          {scoring.scoring_notes && scoring.scoring_notes.length > 0 && (
            <div className="mt-4 pt-3 border-t border-slate-700">
              <p className="text-xs text-slate-500 mb-1">Notes</p>
              {scoring.scoring_notes.map((note, i) => (
                <p key={i} className="text-xs text-slate-400">{note}</p>
              ))}
            </div>
          )}
        </div>

        {/* Velocity Chart */}
        <div className="bg-slate-900/50 border border-slate-800 rounded-xl p-5" data-testid="velocity-chart">
          <h3 className="text-base font-semibold text-white mb-4">Velocity Trend</h3>
          {chartData.length === 0 ? (
            <p className="text-slate-500 text-sm text-center py-8">No velocity data recorded yet</p>
          ) : (
            <ResponsiveContainer width="100%" height={220}>
              <LineChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#252218" />
                <XAxis dataKey="date" tick={{ fill: "#9E9880", fontSize: 11 }} axisLine={{ stroke: "#352F24" }} />
                <YAxis tick={{ fill: "#9E9880", fontSize: 11 }} axisLine={{ stroke: "#352F24" }} />
                <Tooltip
                  contentStyle={{ backgroundColor: "#111008", border: "1px solid #352F24", borderRadius: 8 }}
                  labelStyle={{ color: "#9E9880" }}
                  itemStyle={{ color: "#F59E0B" }}
                />
                <Line type="monotone" dataKey="velocity" stroke="#F59E0B" strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      {/* Intelligence Sections (Tabbed) */}
      <div className="bg-slate-900/50 border border-slate-800 rounded-xl" data-testid="intelligence-section">
        <div className="flex border-b border-slate-800">
          {[
            { key: "funnel", label: "Funnel" },
            { key: "comments", label: "Comments" },
            { key: "creatives", label: "Creatives" },
            { key: "competitors", label: "Competitors" },
            { key: "competitor_ads", label: "Competitor Ads" },
          ].map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveIntelTab(tab.key)}
              data-testid={`intel-tab-${tab.key}`}
              className={`px-5 py-3 text-sm font-medium transition-colors whitespace-nowrap ${
                activeIntelTab === tab.key
                  ? "text-emerald-400 border-b-2 border-emerald-400"
                  : "text-slate-400 hover:text-white"
              }`}
            >
              {tab.label}
              {tab.key === "comments" && intel?.comment_intelligence?.length > 0 && (
                <span className="ml-1.5 text-xs text-slate-500">({intel.comment_intelligence.length})</span>
              )}
              {tab.key === "creatives" && intel?.creative_families?.length > 0 && (
                <span className="ml-1.5 text-xs text-slate-500">({intel.creative_families.length})</span>
              )}
              {tab.key === "competitors" && intel?.competitor_profiles?.length > 0 && (
                <span className="ml-1.5 text-xs text-slate-500">({intel.competitor_profiles.length})</span>
              )}
              {tab.key === "competitor_ads" && competitorAds.length > 0 && (
                <span className="ml-1.5 text-xs text-slate-500">({competitorAds.length})</span>
              )}
            </button>
          ))}
        </div>
        <div className="p-5">
          {activeIntelTab === "funnel" && <FunnelSection funnel={intel?.funnel_deconstruction} />}
          {activeIntelTab === "comments" && <CommentsSection comments={intel?.comment_intelligence || []} />}
          {activeIntelTab === "creatives" && <CreativesSection families={intel?.creative_families || []} />}
          {activeIntelTab === "competitors" && <CompetitorsSection competitors={intel?.competitor_profiles || []} />}
          {activeIntelTab === "competitor_ads" && (
            <CompetitorAdsSection
              ads={competitorAds}
              loading={competitorAdsLoading}
              decomposingId={decomposingId}
              onDecompose={handleDecompose}
            />
          )}
        </div>
      </div>
    </div>
  );
};

// Metric Card
const MetricCard = ({ label, value, sub }) => (
  <div className="bg-slate-900/50 border border-slate-800 rounded-lg p-3">
    <p className="text-xs text-slate-500">{label}</p>
    <p className="text-lg font-bold text-white mt-0.5">{value}</p>
    {sub && <p className="text-xs text-slate-500">{sub}</p>}
  </div>
);

// Score Criterion Bar
const ScoreCriterion = ({ name, score, max, note }) => {
  const pct = max > 0 ? (score / max) * 100 : 0;
  const barColor = pct >= 70 ? "bg-emerald-500" : pct >= 40 ? "bg-blue-500" : pct >= 20 ? "bg-amber-500" : "bg-slate-600";

  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <span className="text-sm text-slate-300">{name}</span>
        <span className="text-sm text-slate-400">
          {score} / {max}
          {note && <span className="text-xs text-slate-500 ml-2">({note})</span>}
        </span>
      </div>
      <div className="w-full bg-slate-800 rounded-full h-1.5">
        <div className={`${barColor} h-1.5 rounded-full transition-all`} style={{ width: `${Math.min(pct, 100)}%` }}></div>
      </div>
    </div>
  );
};

// Funnel Section
const FunnelSection = ({ funnel }) => {
  if (!funnel) {
    return <EmptyIntel message="No funnel deconstruction data available" />;
  }

  const trust = funnel.trust_mechanisms || {};
  const urgency = funnel.urgency_structures || {};
  const social = funnel.social_proof || {};
  const upsells = funnel.upsells || {};
  const guarantee = funnel.guarantee || {};

  return (
    <div className="space-y-4" data-testid="funnel-detail">
      {funnel.landing_page_url && (
        <div className="flex items-center gap-2">
          <span className="text-slate-500 text-sm">Landing Page:</span>
          <a href={funnel.landing_page_url} target="_blank" rel="noopener noreferrer" className="text-emerald-400 hover:text-emerald-300 text-sm truncate">
            {funnel.landing_page_url}
          </a>
        </div>
      )}

      {funnel.copywriting_framework && (
        <div className="inline-flex px-2.5 py-1 rounded bg-purple-500/20 text-purple-400 text-xs">
          Framework: {funnel.copywriting_framework.toUpperCase()}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <IntelCard title="Trust Mechanisms">
          <BoolRow label="Testimonials" value={trust.testimonials} extra={trust.testimonial_count > 0 ? `(${trust.testimonial_count})` : ""} />
          {trust.authority_badges?.length > 0 && <ListRow label="Authority" items={trust.authority_badges} />}
          {trust.media_mentions?.length > 0 && <ListRow label="Media" items={trust.media_mentions} />}
        </IntelCard>

        <IntelCard title="Urgency Structures">
          <BoolRow label="Countdown Timer" value={urgency.countdown_timer} />
          <BoolRow label="Stock Limit" value={urgency.stock_limit} />
          <BoolRow label="Time-Bound Offer" value={urgency.time_bound_offer} />
        </IntelCard>

        <IntelCard title="Social Proof">
          {social.review_count > 0 && <TextRow label="Reviews" value={`${social.review_count} (avg ${social.average_rating || "—"})`} />}
          <TextRow label="Specificity" value={social.specificity_level} />
          <BoolRow label="Video Testimonials" value={social.video_testimonials} />
        </IntelCard>

        <IntelCard title="Upsells & Guarantee">
          <BoolRow label="Has Upsells" value={upsells.has_upsells} extra={upsells.upsell_count > 0 ? `(${upsells.upsell_count})` : ""} />
          <BoolRow label="Order Bump" value={upsells.has_order_bump} extra={upsells.order_bump_price ? `$${upsells.order_bump_price}` : ""} />
          <BoolRow label="Guarantee" value={guarantee.has_guarantee} extra={guarantee.guarantee_length_days ? `${guarantee.guarantee_length_days}d` : ""} />
        </IntelCard>
      </div>

      {funnel.emotional_triggers?.length > 0 && (
        <div>
          <p className="text-xs text-slate-500 mb-1">Emotional Triggers</p>
          <div className="flex flex-wrap gap-1.5">
            {funnel.emotional_triggers.map((t, i) => (
              <span key={i} className="px-2 py-0.5 text-xs bg-slate-800 text-slate-300 rounded">{t}</span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// Comments Section
const CommentsSection = ({ comments }) => {
  if (comments.length === 0) {
    return <EmptyIntel message="No comment intelligence data available" />;
  }

  return (
    <div className="space-y-4" data-testid="comments-detail">
      {comments.map((ci, idx) => {
        const sentiment = ci.sentiment || {};
        const platformBadge = PLATFORM_BADGES[ci.platform] || { label: ci.platform, bg: "bg-slate-500/20", text: "text-slate-400" };

        return (
          <div key={ci.intelligence_id || idx} className="bg-slate-800/30 rounded-lg p-4 space-y-3">
            <div className="flex items-center gap-2">
              <span className={`px-2 py-0.5 rounded text-xs ${platformBadge.bg} ${platformBadge.text}`}>{platformBadge.label}</span>
              <span className="text-xs text-slate-500">{ci.total_comments_analyzed} comments analyzed</span>
            </div>

            {/* Sentiment bar */}
            <div>
              <div className="flex items-center gap-3 text-xs mb-1">
                <span className="text-emerald-400">{sentiment.positive_percentage?.toFixed(0) || 0}% positive</span>
                <span className="text-red-400">{sentiment.negative_percentage?.toFixed(0) || 0}% negative</span>
                <span className="text-slate-400">{sentiment.overall_sentiment}</span>
              </div>
              <div className="flex h-1.5 rounded-full overflow-hidden bg-slate-700">
                <div className="bg-emerald-500" style={{ width: `${sentiment.positive_percentage || 0}%` }}></div>
                <div className="bg-slate-500" style={{ width: `${100 - (sentiment.positive_percentage || 0) - (sentiment.negative_percentage || 0)}%` }}></div>
                <div className="bg-red-500" style={{ width: `${sentiment.negative_percentage || 0}%` }}></div>
              </div>
            </div>

            {/* Objections */}
            {ci.objection_patterns?.length > 0 && (
              <div>
                <p className="text-xs text-slate-500 mb-1">Objections</p>
                {ci.objection_patterns.slice(0, 3).map((obj, i) => (
                  <p key={i} className="text-sm text-slate-300">• {obj.objection} <span className="text-slate-500">({obj.frequency}x)</span></p>
                ))}
              </div>
            )}

            {/* Hidden desires */}
            {ci.hidden_desires?.length > 0 && (
              <div>
                <p className="text-xs text-slate-500 mb-1">Hidden Desires</p>
                {ci.hidden_desires.slice(0, 3).map((d, i) => (
                  <p key={i} className="text-sm text-slate-300">• {d.desire}</p>
                ))}
              </div>
            )}

            {ci.summary && <p className="text-sm text-slate-400 italic">{ci.summary}</p>}
          </div>
        );
      })}
    </div>
  );
};

// Creatives Section
const CreativesSection = ({ families }) => {
  if (families.length === 0) {
    return <EmptyIntel message="No creative families identified yet" />;
  }

  return (
    <div className="space-y-3" data-testid="creatives-detail">
      {families.map((cf) => (
        <div key={cf.family_id} className="bg-slate-800/30 rounded-lg p-4">
          <div className="flex items-start justify-between mb-2">
            <div>
              <p className="text-sm text-white font-medium">{cf.parent_concept}</p>
              <div className="flex items-center gap-2 mt-1">
                {cf.pattern_tags?.map((tag, i) => (
                  <span key={i} className="px-1.5 py-0.5 text-xs bg-slate-700 text-slate-300 rounded">{tag}</span>
                ))}
                {cf.copywriting_framework && (
                  <span className="px-1.5 py-0.5 text-xs bg-purple-500/20 text-purple-400 rounded">{cf.copywriting_framework}</span>
                )}
              </div>
            </div>
            <div className="text-right">
              <p className="text-white font-medium">{cf.variation_count}</p>
              <p className="text-xs text-slate-500">variations</p>
            </div>
          </div>
          {cf.is_scaling && (
            <span className="text-xs text-emerald-400">Scaling — multiple variations detected</span>
          )}
          {cf.evolution_notes?.length > 0 && (
            <div className="mt-2">
              {cf.evolution_notes.slice(0, 2).map((note, i) => (
                <p key={i} className="text-xs text-slate-400">{note}</p>
              ))}
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

// Competitors Section
const CompetitorsSection = ({ competitors }) => {
  if (competitors.length === 0) {
    return <EmptyIntel message="No competitor profiles built yet" />;
  }

  const qualityColors = {
    poor: "text-red-400",
    average: "text-slate-400",
    good: "text-blue-400",
    excellent: "text-emerald-400",
  };

  return (
    <div className="space-y-3" data-testid="competitors-detail">
      {competitors.map((cp) => (
        <div key={cp.profile_id} className="bg-slate-800/30 rounded-lg p-4">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <span className="text-slate-500 text-xs">#{cp.overall_rank}</span>
              <p className="text-sm text-white font-medium">{cp.competitor_name}</p>
            </div>
            <span className={`text-xs ${qualityColors[cp.execution_quality] || "text-slate-400"}`}>
              {cp.execution_quality}
            </span>
          </div>
          <div className="flex items-center gap-4 text-xs text-slate-400">
            <span>{cp.creative_volume} creatives</span>
            <span>Speed: {cp.innovation_speed}</span>
            {cp.inferred_aov && <span>AOV: ~${cp.inferred_aov.toFixed(0)}</span>}
          </div>
          {cp.platforms_active?.length > 0 && (
            <div className="flex gap-1.5 mt-2">
              {cp.platforms_active.map((plat, i) => {
                const badge = PLATFORM_BADGES[plat] || { label: plat, bg: "bg-slate-500/20", text: "text-slate-400" };
                return (
                  <span key={i} className={`px-1.5 py-0.5 rounded text-xs ${badge.bg} ${badge.text}`}>{badge.label}</span>
                );
              })}
            </div>
          )}
          {cp.compliance_workarounds?.length > 0 && (
            <div className="mt-2">
              <p className="text-xs text-amber-400">Compliance workarounds: {cp.compliance_workarounds.join(", ")}</p>
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

// Shared helpers
const EmptyIntel = ({ message }) => (
  <div className="py-8 text-center">
    <p className="text-slate-500 text-sm">{message}</p>
    <p className="text-slate-600 text-xs mt-1">Data populates after platform scans complete</p>
  </div>
);

const IntelCard = ({ title, children }) => (
  <div className="bg-slate-800/30 rounded-lg p-3">
    <p className="text-xs text-slate-500 font-medium mb-2">{title}</p>
    <div className="space-y-1.5">{children}</div>
  </div>
);

const BoolRow = ({ label, value, extra }) => (
  <div className="flex items-center justify-between text-sm">
    <span className="text-slate-400">{label}</span>
    <span className={value ? "text-emerald-400" : "text-slate-600"}>
      {value ? "Yes" : "No"} {extra && <span className="text-slate-500 text-xs ml-1">{extra}</span>}
    </span>
  </div>
);

const TextRow = ({ label, value }) => (
  <div className="flex items-center justify-between text-sm">
    <span className="text-slate-400">{label}</span>
    <span className="text-slate-300">{value || "—"}</span>
  </div>
);

const ListRow = ({ label, items }) => (
  <div className="text-sm">
    <span className="text-slate-400">{label}: </span>
    <span className="text-slate-300">{items.join(", ")}</span>
  </div>
);

// Competitor Ads Section (Swipekit)
const CompetitorAdsSection = ({ ads, loading, decomposingId, onDecompose }) => {
  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-emerald-500"></div>
        <span className="ml-3 text-slate-400 text-sm">Searching Swipekit for competitor ads...</span>
      </div>
    );
  }

  if (!ads || ads.length === 0) {
    return <EmptyIntel message="No competitor ads found via Swipekit for this offer's vertical" />;
  }

  const platformColors = {
    facebook: "bg-blue-500/20 text-blue-400",
    "instagram-feed": "bg-pink-500/20 text-pink-400",
    tiktok: "bg-purple-500/20 text-purple-400",
    linkedin: "bg-sky-500/20 text-sky-400",
  };

  return (
    <div className="space-y-3" data-testid="competitor-ads-section">
      <p className="text-xs text-slate-500 mb-2">
        Showing {ads.length} competitor ads from Swipekit &middot; Sorted by active days
      </p>
      {ads.map((ad) => (
        <div key={ad.swipekit_id} className="bg-slate-800/30 rounded-lg p-4 space-y-3">
          <div className="flex items-start gap-3">
            {ad.thumbnail_url && (
              <img
                src={ad.thumbnail_url}
                alt=""
                className="w-16 h-16 rounded-lg object-cover flex-shrink-0 bg-slate-700"
                onError={(e) => { e.target.style.display = 'none'; }}
              />
            )}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <span className="text-sm text-white font-medium truncate">{ad.advertiser || "Unknown"}</span>
                <span className={`px-1.5 py-0.5 rounded text-xs ${platformColors[ad.platform] || "bg-slate-500/20 text-slate-400"}`}>
                  {ad.platform}
                </span>
                {ad.status === "ACTIVE" && (
                  <span className="px-1.5 py-0.5 rounded text-xs bg-emerald-500/20 text-emerald-400">Active</span>
                )}
              </div>
              <div className="flex items-center gap-3 text-xs text-slate-400">
                <span>{ad.active_days || 0} days running</span>
                <span>{ad.asset_type}</span>
                {ad.cta_type && <span>CTA: {ad.cta_type}</span>}
              </div>
            </div>
          </div>

          {ad.ad_copy && (
            <p className="text-sm text-slate-300 leading-relaxed line-clamp-3">{ad.ad_copy.slice(0, 200)}{ad.ad_copy.length > 200 ? "..." : ""}</p>
          )}

          {ad.decomposition ? (
            <div className="bg-slate-900/50 rounded-lg p-3 space-y-2">
              <p className="text-xs text-emerald-400 font-medium">Creative Decomposition</p>
              {ad.decomposition.summary && (
                <p className="text-xs text-slate-300 italic">{ad.decomposition.summary}</p>
              )}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 text-xs">
                {ad.decomposition.vehicle && (
                  <div>
                    <p className="text-slate-500 mb-0.5">Vehicle</p>
                    <p className="text-slate-300">{ad.decomposition.vehicle.format} / {ad.decomposition.vehicle.structure}</p>
                  </div>
                )}
                {ad.decomposition.message && (
                  <div>
                    <p className="text-slate-500 mb-0.5">Message</p>
                    <p className="text-slate-300">{ad.decomposition.message.hook_type} / {ad.decomposition.message.emotion}</p>
                  </div>
                )}
                {ad.decomposition.visual && (
                  <div>
                    <p className="text-slate-500 mb-0.5">Visual</p>
                    <p className="text-slate-300">{ad.decomposition.visual.composition} / {ad.decomposition.visual.text_overlay} overlay</p>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <button
              onClick={() => onDecompose(ad.swipekit_id)}
              disabled={decomposingId === ad.swipekit_id}
              className="px-3 py-1.5 text-xs bg-slate-700/50 text-slate-300 rounded-lg hover:bg-slate-700 transition-colors disabled:opacity-50"
              data-testid={`decompose-btn-${ad.swipekit_id}`}
            >
              {decomposingId === ad.swipekit_id ? "Analyzing..." : "Decompose Creative"}
            </button>
          )}
        </div>
      ))}
    </div>
  );
};

export default GhostReconOfferDetail;
